import React, { useState, useEffect } from "react";
import { Alert, Form } from "react-bootstrap";
import Autocomplete, { ReactGoogleAutocompleteInputProps } from "react-google-autocomplete";
import {
    MdAddLocationAlt,
    MdClose,
    MdDelete,
    MdErrorOutline,
    MdLocationOn,
    MdSchedule,
    MdSearch,
    MdStorefront,
} from "react-icons/md";
import "./Collections.css";
import WeeklyHours from "./WeeklyHours";
import LeadTime from "./LeadTime";
import { DISPLAY_NAME_MAX_LENGTH, normaliseDisplayName } from "./displayName";
import {
    Availability,
    CollectionPoint,
    GetCollectionPointsInput,
    GetCollectionPointsOutput,
    CreateCollectionPointInput,
    CreateCollectionPointOutput,
    UpdateCollectionPointInput,
    UpdateCollectionPointOutput,
    DeleteCollectionPointInput,
    DeleteCollectionPointOutput,
} from "../../client/core";
import { Auth } from "aws-amplify";

// react-google-autocomplete types its input against HTMLAttributes, which omits
// value/placeholder — widen it so the address field can be controlled.
const AddressAutocomplete = Autocomplete as unknown as React.FC<
    ReactGoogleAutocompleteInputProps & React.InputHTMLAttributes<HTMLInputElement>
>;

const DEFAULT_AVAILABILITY: Availability = {
    weekly: [],
    overrides: [],
    leadTimeHours: 48
};

const DAYS: { key: string; short: string; initial: string }[] = [
    { key: "monday", short: "Mon", initial: "M" },
    { key: "tuesday", short: "Tue", initial: "T" },
    { key: "wednesday", short: "Wed", initial: "W" },
    { key: "thursday", short: "Thu", initial: "T" },
    { key: "friday", short: "Fri", initial: "F" },
    { key: "saturday", short: "Sat", initial: "S" },
    { key: "sunday", short: "Sun", initial: "S" },
];

// The API stores a single address string. The design shows a street line above a
// town/postcode line, so split on the first comma and treat the rest as line two.
export const splitAddress = (address: string): { line1: string; line2: string } => {
    const separator = address.indexOf(",");

    if (separator === -1) {
        return { line1: address.trim(), line2: "" };
    }

    return {
        line1: address.slice(0, separator).trim(),
        line2: address.slice(separator + 1).trim()
    };
};

// Points seeded from the shop's own locations carry an address id. Their address
// is owned by the shop record, so it is fixed here and they cannot be deleted.
export const isShopLocation = (point: CollectionPoint): boolean =>
    !!point.addressId;

const formatLeadTime = (hours: number): string =>
    hours === 0 ? "No notice needed" : `${hours} hours notice`;

const formatDayHours = (availability: Availability, day: string): string => {
    const times = availability?.weekly?.find(w => w.day === day)?.times || [];

    if (times.length === 0) {
        return "Open all day";
    }

    return times.map(t => `${t.start}–${t.end}`).join(", ");
};

const isDayOpen = (availability: Availability, day: string): boolean =>
    (availability?.weekly || []).some(w => w.day === day);

export interface CollectionPointsProps {
    shopId: string;
    auth: typeof Auth;
    getCollectionPoints: (input: GetCollectionPointsInput) => Promise<GetCollectionPointsOutput | Error>;
    createCollectionPoint: (input: CreateCollectionPointInput) => Promise<CreateCollectionPointOutput | Error>;
    updateCollectionPoint: (input: UpdateCollectionPointInput) => Promise<UpdateCollectionPointOutput | Error>;
    deleteCollectionPoint: (input: DeleteCollectionPointInput) => Promise<DeleteCollectionPointOutput | Error>;
    /** Google Places key for the address search. */
    placesApiKey?: string;
}

type FormMode = "list" | "add" | "edit";

const CollectionPoints: React.FC<CollectionPointsProps> = ({
    shopId,
    auth,
    getCollectionPoints,
    createCollectionPoint,
    updateCollectionPoint,
    deleteCollectionPoint,
    placesApiKey,
}) => {
    const [points, setPoints] = useState<CollectionPoint[]>([]);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");
    const [mode, setMode] = useState<FormMode>("list");
    const [editingPointId, setEditingPointId] = useState<string | null>(null);
    const [draftAddress, setDraftAddress] = useState<string>("");
    const [draftDisplayName, setDraftDisplayName] = useState<string>("");
    const [draftAvailability, setDraftAvailability] = useState<Availability>(DEFAULT_AVAILABILITY);
    const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState<boolean>(false);

    useEffect(() => {
        const fetchPoints = async () => {
            const response = await getCollectionPoints({ shopId });

            if (response instanceof Error) {
                setError("Failed to load collection points. If this issue persists please contact: support@stumbled.online");
                return;
            }

            setPoints(response.body?.collectionPoints || []);
        };

        fetchPoints();
    }, [shopId, getCollectionPoints]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const startAdd = () => {
        setError("");
        setSuccess("");
        setShowValidation(false);
        setEditingPointId(null);
        setDraftAddress("");
        setDraftDisplayName("");
        setDraftAvailability(DEFAULT_AVAILABILITY);
        setMode("add");
    };

    const startEdit = (point: CollectionPoint) => {
        setError("");
        setSuccess("");
        setShowValidation(false);
        setConfirmingDeleteId(null);
        setEditingPointId(point.id);
        setDraftAddress(point.address);
        setDraftDisplayName(point.displayName ?? "");
        setDraftAvailability(point.availability);
        setMode("edit");
    };

    const cancelForm = () => {
        setMode("list");
        setEditingPointId(null);
        setShowValidation(false);
    };

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (draftAddress.trim() === "") {
            setShowValidation(true);
            return;
        }

        const session = await auth.currentSession();
        const identityToken = session.getIdToken().getJwtToken();
        const displayName = normaliseDisplayName(draftDisplayName);

        if (mode === "edit" && editingPointId) {
            const response = await updateCollectionPoint({
                identityToken,
                shopId,
                pointId: editingPointId,
                address: draftAddress,
                availability: draftAvailability,
                displayName,
            });

            if (response instanceof Error) {
                setError("The collection point could not be updated at this time. If this issue persists please contact: support@stumbled.online");
                return;
            }

            if (response.body?.collectionPoint) {
                const updated = response.body.collectionPoint;
                setPoints(points.map(p => p.id === updated.id ? updated : p));
            }

            setSuccess("Your changes have been saved.");
            setMode("list");
            setEditingPointId(null);
            return;
        }

        const response = await createCollectionPoint({
            identityToken,
            shopId,
            address: draftAddress,
            availability: draftAvailability,
            displayName,
        });

        if (response instanceof Error) {
            setError("The collection point could not be created at this time. If this issue persists please contact: support@stumbled.online");
            return;
        }

        if (response.body?.collectionPoint) {
            setPoints([...points, response.body.collectionPoint]);
        }

        setSuccess("Your changes have been saved.");
        setMode("list");
    };

    const handleDeleteClick = (pointId: string) => {
        setError("");
        setSuccess("");
        setConfirmingDeleteId(pointId);
    };

    const cancelDelete = () => {
        setConfirmingDeleteId(null);
    };

    const confirmDelete = async (pointId: string) => {
        const session = await auth.currentSession();
        const identityToken = session.getIdToken().getJwtToken();

        const response = await deleteCollectionPoint({
            identityToken,
            shopId,
            pointId,
        });

        if (response instanceof Error) {
            setError("The collection point could not be deleted at this time. If this issue persists please contact: support@stumbled.online");
            setConfirmingDeleteId(null);
            return;
        }

        setPoints(points.filter(p => p.id !== pointId));
        setConfirmingDeleteId(null);
        setSuccess("The collection point has been deleted.");
    };

    const renderCard = (point: CollectionPoint) => {
        const { line1, line2 } = splitAddress(point.address);
        const premises = isShopLocation(point);
        const openDays = DAYS.filter(d => isDayOpen(point.availability, d.key));

        return (
            <div key={point.id} className="cp-card" data-testid={`collection-point-${point.id}`}>
                <div className="cp-card-head">
                    <span className="cp-card-icon">
                        {premises ? <MdStorefront size={22} /> : <MdLocationOn size={22} />}
                    </span>
                    <div className="cp-card-headings">
                        <div className="cp-card-titles">
                            <span className="cp-card-title">{line1}</span>
                            {premises && <span className="cp-badge">Shop premises</span>}
                        </div>
                        {line2 !== "" && <div className="cp-card-sub">{line2}</div>}
                    </div>
                </div>

                <div className="cp-chips">
                    {DAYS.map(day => {
                        const open = isDayOpen(point.availability, day.key);
                        return (
                            <span
                                key={day.key}
                                className={`cp-chip ${open ? "cp-chip-on" : "cp-chip-off"}`}
                                title={`${day.short}: ${open ? formatDayHours(point.availability, day.key) : "Closed"}`}
                            >
                                {day.initial}
                            </span>
                        );
                    })}
                </div>

                <div className="cp-open-lines">
                    {openDays.map(day => (
                        <div key={day.key} className="cp-open-line">
                            <span className="cp-open-day">{day.short}</span>
                            <span>{formatDayHours(point.availability, day.key)}</span>
                        </div>
                    ))}
                    {openDays.length === 0 && (
                        <div className="cp-closed-warning">
                            <MdErrorOutline size={16} />
                            No days selected — customers can&apos;t collect here
                        </div>
                    )}
                </div>

                <div className="cp-card-foot">
                    <span className="cp-lead-chip">
                        <MdSchedule size={15} />
                        {formatLeadTime(point.availability?.leadTimeHours ?? 0)}
                    </span>

                    <div className="cp-card-actions">
                        {premises ? (
                            <button
                                type="button"
                                className="cp-action-edit"
                                aria-label={`Edit ${point.address}`}
                                onClick={() => startEdit(point)}
                            >
                                Edit
                            </button>
                        ) : confirmingDeleteId === point.id ? (
                            <>
                                <button
                                    type="button"
                                    className="cp-btn-danger"
                                    onClick={() => confirmDelete(point.id)}
                                >
                                    Confirm delete
                                </button>
                                <button
                                    type="button"
                                    className="cp-action-cancel"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="cp-action-edit"
                                    aria-label={`Edit ${point.address}`}
                                    onClick={() => startEdit(point)}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="cp-action-delete"
                                    aria-label={`Delete ${point.address}`}
                                    onClick={() => handleDeleteClick(point.id)}
                                >
                                    <MdDelete size={18} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const editingPoint = points.find(p => p.id === editingPointId);
    const editingShopLocation = !!editingPoint && isShopLocation(editingPoint);
    const addressInvalid = showValidation && draftAddress.trim() === "";

    return (
        <div className="cp-page">
            <div className="cp-page-head">
                <h1 className="cp-title">Collections</h1>
                <button type="button" className="cp-btn-primary" onClick={startAdd}>
                    <MdAddLocationAlt size={20} />
                    Add collection point
                </button>
            </div>

            <p className="cp-lede">
                Customers choose one of these locations when they collect an order. Each point keeps its own
                address, lead time and weekly hours.
            </p>

            <Alert variant="danger" show={error !== ""}>
                {error}
            </Alert>
            <Alert variant="success" show={success !== ""}>
                {success}
            </Alert>

            {points.length === 0 ? (
                <div className="cp-empty">
                    <span className="cp-empty-icon">
                        <MdStorefront size={40} />
                    </span>
                    <div className="cp-empty-title">No collection points yet</div>
                    <div className="cp-empty-body">
                        Add your shop premises first, then any drop-off locations you use.
                    </div>
                    <button type="button" className="cp-btn-primary" onClick={startAdd}>
                        Add collection point
                    </button>
                </div>
            ) : (
                <div className="cp-grid">
                    {points.map(renderCard)}
                </div>
            )}

            {mode !== "list" && (
                <>
                    <div className="cp-scrim" onClick={cancelForm} data-testid="drawer-scrim" />
                    <div className="cp-drawer" role="dialog" aria-label={mode === "edit" ? "Edit collection point" : "New collection point"}>
                        <div className="cp-drawer-head">
                            <button
                                type="button"
                                className="cp-drawer-close"
                                onClick={cancelForm}
                                aria-label="Close"
                            >
                                <MdClose size={22} />
                            </button>
                            <span className="cp-drawer-title">
                                {mode === "edit" ? "Edit collection point" : "New collection point"}
                            </span>
                        </div>

                        <div className="cp-drawer-body">
                            <div className="cp-field">
                                <label className="cp-field-label" htmlFor="cp-address">Address</label>
                                {editingShopLocation ? (
                                    <>
                                        <div className="cp-static-address" data-testid="static-address">
                                            <MdStorefront size={20} />
                                            {draftAddress}
                                        </div>
                                        <p className="cp-field-hint">
                                            This is one of your shop locations. Its address is managed in your shop
                                            settings, but you can set its collection hours here.
                                        </p>
                                    </>
                                ) : (
                                <div className="cp-input-wrap">
                                    <span className="cp-input-icon">
                                        <MdSearch size={20} />
                                    </span>
                                    <AddressAutocomplete
                                        id="cp-address"
                                        className="cp-address-input"
                                        apiKey={placesApiKey}
                                        placeholder="Search for an address"
                                        value={draftAddress}
                                        onChange={(e) => setDraftAddress((e.target as HTMLInputElement).value)}
                                        onPlaceSelected={(place) => {
                                            setDraftAddress(place?.formatted_address || draftAddress);
                                        }}
                                        options={{ types: ["address"] }}
                                    />
                                </div>
                                )}
                                {addressInvalid && (
                                    <div className="cp-field-error" data-testid="address-error">
                                        <MdErrorOutline size={15} />
                                        Enter the address customers will collect from
                                    </div>
                                )}
                            </div>

                            <Form.Group className="cp-field" controlId="cp-display-name">
                                <Form.Label className="cp-field-label">Display name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. Saturday market stall"
                                    maxLength={DISPLAY_NAME_MAX_LENGTH}
                                    value={draftDisplayName}
                                    onChange={(e) => setDraftDisplayName(e.target.value)}
                                    aria-describedby="cp-display-name-hint"
                                />
                                <Form.Text id="cp-display-name-hint" className="cp-field-hint">
                                    Optional. Customers see this name at checkout, with the address underneath.
                                </Form.Text>
                            </Form.Group>

                            <LeadTime
                                availability={draftAvailability}
                                onChange={setDraftAvailability}
                            />

                            <WeeklyHours
                                availability={draftAvailability}
                                onChange={setDraftAvailability}
                            />
                        </div>

                        <div className="cp-drawer-foot">
                            <button type="button" className="cp-btn-text" onClick={cancelForm}>
                                Cancel
                            </button>
                            <button type="button" className="cp-btn-primary" onClick={handleSave}>
                                {mode === "edit" ? "Save changes" : "Add point"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CollectionPoints;
